import React, { useEffect, useState } from 'react'
import {
  GRAY_COLORS_VARIANTS,
  PRIMARY_COLORS_VARIANTS,
  TEXT_DARK_COLORS_VARIANTS,
} from '@/utils/constants/colors'
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { toast } from 'react-hot-toast'

import { MainDrawer } from 'components/shared/MainDrawer'
import { ValidNickname } from '@/lib/validations'
import { PROFILE_SERVICE_ERRORS } from 'api/constants'
import { useAuth } from 'context/JWTContext'
import { useUpdateProfile } from 'hooks/useUpdateProfile'
import { DarkInputField } from '../DarkInputField'

interface ChangeNicknameDrawerProps {
  currentNickname?: string
  open: boolean
  onClose: () => void
  onOpen?: () => void
}

export const ChangeNicknameDrawer = ({
  currentNickname = '',
  open,
  onClose,
  onOpen,
}: ChangeNicknameDrawerProps) => {
  const { reloadUser } = useAuth()
  const [nickname, setNickname] = useState<string>(currentNickname)
  const { mutate, isLoading } = useUpdateProfile({ nickname: nickname.trim() })

  useEffect(() => {
    if (open) setNickname(currentNickname)
  }, [open, currentNickname])

  const validatedNickname = ValidNickname.safeParse(nickname.trim())
  const showValidationError = !validatedNickname.success && nickname.length > 0
  const isSaveDisabled =
    !validatedNickname.success || nickname.trim() === currentNickname || isLoading

  const handleSave = () => {
    mutate(undefined, {
      onSuccess: () => {
        reloadUser()
        toast.success('Name has been updated successfully', {
          position: 'top-center',
          style: { background: '#00BFA6', color: '#fff' },
        })
        onClose()
      },
      onError: (e: any) =>
        toast.error(
          PROFILE_SERVICE_ERRORS[e.response?.data?.detail as keyof typeof PROFILE_SERVICE_ERRORS] ??
            'Unable to update your name. Please try again.',
        ) as unknown as void,
    })
  }

  return (
    <MainDrawer open={open} onClose={onClose} onOpen={onOpen}>
      <Box
        component="form"
        display="flex"
        flexDirection="column"
        gap={4}
        p={2}
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          if (!isSaveDisabled) handleSave()
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography
              variant="h6"
              component="h1"
              color={TEXT_DARK_COLORS_VARIANTS[400]}
              fontWeight={700}
            >
              Change nickname
            </Typography>
            <Typography
              variant="body2"
              component="p"
              color={TEXT_DARK_COLORS_VARIANTS[400]}
              fontSize="0.875rem"
              lineHeight="1rem"
              sx={{ opacity: 0.8 }}
            >
              This is the name other players see on the leaderboards.
            </Typography>
          </Box>
          <IconButton
            aria-label="Close change nickname drawer"
            size="small"
            onClick={onClose}
            sx={{ mt: 0.5 }}
          >
            <CloseIcon sx={{ fontSize: '20px' }} />
          </IconButton>
        </Box>
        <Box display="flex" flexDirection="column" gap={1}>
          <DarkInputField
            fullWidth
            inputMode="text"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="Enter your nickname"
            disabled={isLoading}
            inputProps={{ maxLength: 20 }}
          />
          {showValidationError && (
            <Typography color="error.main" fontSize="0.875rem">
              {validatedNickname.error.errors[0].message}
            </Typography>
          )}
        </Box>
        <Button
          fullWidth
          type="submit"
          variant="contained"
          disabled={isSaveDisabled}
          endIcon={
            isLoading ? (
              <CircularProgress size={16} sx={{ color: TEXT_DARK_COLORS_VARIANTS[400] }} />
            ) : undefined
          }
          sx={{
            py: 1.5,
            borderRadius: '0.75rem',
            fontWeight: 700,
            fontSize: '0.9375rem',
            textTransform: 'none',
            bgcolor: PRIMARY_COLORS_VARIANTS[500],
            color: TEXT_DARK_COLORS_VARIANTS[400],
            boxShadow: 'none',
            '&:hover': { bgcolor: PRIMARY_COLORS_VARIANTS[600], boxShadow: 'none' },
            '&:disabled': {
              bgcolor: GRAY_COLORS_VARIANTS[100],
              color: TEXT_DARK_COLORS_VARIANTS[350],
            },
          }}
        >
          {isLoading ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </MainDrawer>
  )
}
